import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "relative inline-flex h-fit items-center justify-center whitespace-nowrap transition-all [&_svg]:pointer-events-none [&_svg]:shrink-0 shrink-0 outline-none select-none",
  {
    variants: {
      theme: {
        // Primary Gradient (Figma: Theme=Gradient)
        gradient: "",
        // Primary Normal (Figma: Theme=Normal)
        normal: "",
        // Assistive (Figma: Theme=Assistive)
        assistive: "",
      },
      variant: {
        // Filled (default)
        default: "",
        // Outline
        outline: "",
        // Legacy variants (shadcn/ui compatibility)
        destructive:
          "bg-destructive text-white inset-border-double hover:bg-destructive/90",
        secondary:
          "bg-secondary text-secondary-foreground inset-border-double hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        // Figma Design System sizes
        lg: "px-4.5 py-3 gap-2 rounded-xl typo-body-1-semibold [&_svg]:size-6",
        md: "px-4 py-2.5 gap-1.5 rounded-[10px] typo-body-2-semibold [&_svg]:size-5",
        sm: "px-3 py-2 gap-1 rounded-md typo-label-1-semibold [&_svg]:size-4 h-9",
        xs: "px-2 py-[7px] gap-1 rounded-sm typo-caption-1-semibold [&_svg]:size-4",
        // Icon only sizes
        icon: "size-11 rounded-[10px] [&_svg]:size-5",
        "icon-lg": "size-[52px] rounded-xl [&_svg]:size-6",
        "icon-sm": "size-9 rounded-md [&_svg]:size-4",
        "icon-xs": "size-7 rounded-sm [&_svg]:size-4",
      },
    },
    defaultVariants: {
      theme: "normal",
      variant: "default",
      size: "md",
    },
  },
);

// Solid button styles (default variant)
const solidStyles = {
  gradient:
    "bg-primary-gradient text-static-white inset-border-double hover:after:opacity-[0.06] focus-visible:after:opacity-10 active:after:opacity-[0.14] after:absolute after:inset-0 after:bg-static-black after:opacity-0 after:rounded-[inherit] after:transition-opacity",
  normal:
    "bg-primary-normal text-static-white inset-border-double hover:after:opacity-[0.06] focus-visible:after:opacity-10 active:after:opacity-[0.14] after:absolute after:inset-0 after:bg-static-black after:opacity-0 after:rounded-[inherit] after:transition-opacity",
  assistive:
    "bg-fills-neutral text-labels-primary inset-border-double hover:after:opacity-[0.02] focus-visible:after:opacity-[0.06] active:after:opacity-10 after:absolute after:inset-0 after:bg-static-black after:opacity-0 after:rounded-[inherit] after:transition-opacity",
};

// Outline button styles
const outlineStyles = {
  gradient:
    "bg-static-white text-primary-normal inset-outline-border-gradient bg-gradient-overlay",
  normal:
    "bg-static-white text-primary-normal inset-border-primary-normal hover:after:opacity-[0.06] focus-visible:after:opacity-10 active:after:opacity-[0.14] after:absolute after:inset-0 after:bg-primary-strong after:opacity-0 after:rounded-[inherit] after:transition-opacity",
  assistive:
    "bg-static-white text-labels-primary inset-border-strong hover:after:opacity-[0.02] focus-visible:after:opacity-[0.06] active:after:opacity-10 after:absolute after:inset-0 after:bg-static-black after:opacity-0 after:rounded-[inherit] after:transition-opacity",
};

const disabledBorderStyles = {
  // solid 비활성은 배경도 회색으로 내린다 — theme 별 배경(gradient·primary)을 덮어야 한다
  solid: "inset-border-double bg-fills-neutral",
  outline: "inset-border-strong bg-fills-neutral",
};

function Button({
  className,
  theme = "normal",
  variant = "default",
  size = "md",
  asChild = false,
  disabled = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  const isOutline = variant === "outline";
  const themeKey = theme ?? "normal";
  const themeStyle = isOutline
    ? outlineStyles[themeKey]
    : solidStyles[themeKey];
  const disabledStyle = cn(
    disabledBorderStyles[isOutline ? "outline" : "solid"],
    "text-labels-assistive cursor-not-allowed pointer-events-none",
  );

  return (
    <Comp
      data-slot="button"
      className={cn(
        buttonVariants({ theme, variant, size }),
        // theme 배경을 아예 붙이지 않는다 — bg-primary-gradient 는 background 단축 속성이라
        // 뒤에 bg-fills-neutral 을 얹어도 그라디언트가 남는다
        disabled ? disabledStyle : themeStyle,
        className,
      )}
      {...props}
    />
  );
}

export { Button, buttonVariants };
